import { fundingCountryCodes } from './country-codes.js';
export const customerKycChoices = {
    employmentStatus: [
        'employedOrSelfEmployed',
        'retired',
        'student',
        'unemployed',
    ],
    accountPurpose: [
        'investing',
        'payments',
        'purchasingDigitalAssets',
        'purchasingGoodsOrServices',
        'trading',
    ],
    sourceOfWealth: [
        'salary',
        'savings',
        'investments',
        'cryptoTrading',
        'other',
    ],
    profession: [
        'agriculture',
        'artsAndEntertainment',
        'businessOwner',
        'education',
        'financialServices',
        'healthcare',
        'industrialTradesAndTransport',
        'informationAndTechnology',
        'legalAndProfessionalServices',
        'publicSector',
        'realEstate',
        'retail',
        'seniorManagement',
    ],
    reportingCurrency: ['AUD', 'EUR', 'GBP', 'USD'],
};
export function extendedKycFields(category, field) {
    if (category === 'taxIdentifiers') {
        if (field === 'ssn' || field === 'cpf')
            return [field];
        if (field === 'tin')
            return ['tinCountry', 'tin'];
    }
    if (category === 'questionnaires') {
        if (field === 'customerDueDiligence')
            return [
                'reportingCountry',
                'reportingCurrency',
                'employmentStatus',
                'accountPurpose',
                'grossAnnualIncome',
                'sourceOfWealth',
                'transactionFrequencyPerMonth',
                'expectedTransactionAmountPerMonth',
            ];
        if (field === 'enhancedDueDiligence')
            return ['reportingCountry', 'reportingCurrency', 'profession', 'netWorth'];
    }
    return null;
}
export function customerKycOptions(field) {
    return field in customerKycChoices
        ? customerKycChoices[field]
        : null;
}
export function isKycCountryField(field) {
    return ['country', 'nationality', 'tinCountry', 'reportingCountry'].includes(field);
}
export function isKycTaxField(field) {
    return ['ssn', 'cpf', 'tin'].includes(field);
}
export function isKycMoneyField(field) {
    return ['grossAnnualIncome', 'expectedTransactionAmountPerMonth', 'netWorth'].includes(field);
}
export function validExtendedKycField(field, value) {
    const choices = customerKycOptions(field);
    if (choices)
        return choices.includes(value);
    if (field === 'ssn')
        return /^(\d{9}|\d{3}-\d{2}-\d{4})$/.test(value);
    if (field === 'cpf')
        return /^(\d{11}|\d{3}\.\d{3}\.\d{3}-\d{2})$/.test(value);
    if (field === 'tin')
        return value.length > 0;
    if (field === 'transactionFrequencyPerMonth')
        return /^\d+$/.test(value) && BigInt(value) > 0n && BigInt(value) <= 2147483647n;
    if (isKycMoneyField(field))
        return /^\d+(\.\d{1,2})?$/.test(value) && /[1-9]/.test(value);
    return undefined;
}
/** Reporting policy from MoonPay's Customer API, never inferred from device/IP. */
export function customerReportingCurrency(country) {
    if (!fundingCountryCodes.some(({ alpha3 }) => alpha3 === country))
        return null;
    if (country === 'GBR')
        return 'GBP';
    if (country === 'AUS' || country === 'NZL')
        return 'AUD';
    if ('AUT BEL BGR HRV CYP CZE DNK EST FIN FRA DEU GRC HUN ISL IRL ITA LVA LIE LTU LUX MLT NLD NOR POL PRT ROU SVK SVN ESP SWE'
        .split(' ')
        .includes(country))
        return 'EUR';
    return 'USD';
}
export function extendedKycBody(fields, draft) {
    const body = {};
    const taxes = [];
    for (const type of ['ssn', 'cpf', 'tin']) {
        if (!fields.includes(type))
            continue;
        const value = required(draft, type);
        taxes.push(type === 'tin' ? { type, value, country: required(draft, 'tinCountry') } : { type, value });
    }
    if (taxes.length)
        body.taxIdentifiers = taxes;
    const questionnaires = [];
    const reportingCountry = fields.includes('country') ? draft.country : draft.reportingCountry;
    if (fields.includes('employmentStatus'))
        questionnaires.push({
            type: 'customerDueDiligence',
            answers: {
                employmentStatus: choice('employmentStatus', draft),
                accountPurpose: choice('accountPurpose', draft),
                grossAnnualIncome: monetary('grossAnnualIncome', draft, reportingCountry),
                sourceOfWealth: choice('sourceOfWealth', draft),
                transactionFrequencyPerMonth: Number(required(draft, 'transactionFrequencyPerMonth')),
                expectedTransactionAmountPerMonth: monetary('expectedTransactionAmountPerMonth', draft, reportingCountry),
            },
        });
    if (fields.includes('profession'))
        questionnaires.push({
            type: 'enhancedDueDiligence',
            answers: {
                profession: choice('profession', draft),
                netWorth: monetary('netWorth', draft, reportingCountry),
            },
        });
    if (questionnaires.length)
        body.questionnaires = questionnaires;
    return body;
}
function required(draft, field) {
    const value = draft[field]?.trim();
    if (!value)
        throw new Error('kyc_invalid_fields');
    return value;
}
function choice(field, draft) {
    const value = required(draft, field);
    const found = customerKycChoices[field].find((candidate) => candidate === value);
    if (!found)
        throw new Error('kyc_invalid_fields');
    return found;
}
function monetary(field, draft, country) {
    const currency = choice('reportingCurrency', draft);
    if (currency !== customerReportingCurrency(country ?? ''))
        throw new Error('kyc_invalid_fields');
    return { currency, amount: required(draft, field) };
}
